import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "../../utils/axiosInstance";
import {
  Wallet,
  CheckCircle,
  Clock,
  FunnelSimple,
  ArrowsCounterClockwise,
  Plus,
  CircleNotch,
  X,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import Pagination from "../shared/Pagination";

const API = `${window.location.origin}/api`;

const MONTHS_PL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

const fmtPLN = (v) =>
  Number(v || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function getTargetOptions(refDate) {
  if (!refDate) return [];
  const [y, m] = refDate.split("-").map(Number);
  const prevMonth = m === 1 ? 12 : m - 1;
  const prevYear = m === 1 ? y - 1 : y;
  const nextMonth = m === 12 ? 1 : m + 1;
  const nextYear = m === 12 ? y + 1 : y;
  return [
    { year: prevYear, month: prevMonth, period: "01-15", label: `${MONTHS_PL[prevMonth - 1]} ${prevYear} — 01-15` },
    { year: prevYear, month: prevMonth, period: "16-30", label: `${MONTHS_PL[prevMonth - 1]} ${prevYear} — 16-30` },
    { year: y, month: m, period: "01-15", label: `${MONTHS_PL[m - 1]} ${y} — 01-15` },
    { year: y, month: m, period: "16-30", label: `${MONTHS_PL[m - 1]} ${y} — 16-30` },
    { year: nextYear, month: nextMonth, period: "01-15", label: `${MONTHS_PL[nextMonth - 1]} ${nextYear} — 01-15` },
    { year: nextYear, month: nextMonth, period: "16-30", label: `${MONTHS_PL[nextMonth - 1]} ${nextYear} — 16-30` },
  ];
}

export default function PozyczkiPracownikowTab() {
  const [drafts, setDrafts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    employee_id: "",
    data: today,
    kwota: "",
    note: "",
    targetIdx: 4,  // default: next month 01-15
  });
  const [saving, setSaving] = useState(false);
  const targetOptions = useMemo(() => getTargetOptions(form.data), [form.data]);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterEmployee ? { employee_id: filterEmployee } : {};
      const { data } = await axios.get(`${API}/employee-pozyczka`, { params });
      setDrafts(data || []);
    } catch (err) {
      console.error("Error fetching pożyczka drafts:", err);
    } finally {
      setLoading(false);
    }
  }, [filterEmployee]);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employees`);
      setEmployees(data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  useEffect(() => {
    const t = setInterval(fetchDrafts, 30000);
    return () => clearInterval(t);
  }, [fetchDrafts]);

  const filtered = useMemo(() => {
    return drafts.filter((d) => {
      if (filterStatus === "pending" && d.applied) return false;
      if (filterStatus === "applied" && !d.applied) return false;
      if (filterDateFrom && d.data && d.data < filterDateFrom) return false;
      if (filterDateTo && d.data && d.data > filterDateTo) return false;
      return true;
    });
  }, [drafts, filterStatus, filterDateFrom, filterDateTo]);

  useEffect(() => {
    setPage(1);
  }, [filterEmployee, filterDateFrom, filterDateTo, filterStatus, pageSize]);

  const paginated = useMemo(() => {
    const s = (page - 1) * pageSize;
    return filtered.slice(s, s + pageSize);
  }, [filtered, page, pageSize]);

  const totals = useMemo(() => {
    const sum = filtered.reduce((s, d) => s + Number(d.kwota || 0), 0);
    const pendingSum = filtered
      .filter((d) => !d.applied)
      .reduce((s, d) => s + Number(d.kwota || 0), 0);
    const applied = filtered.filter((d) => d.applied).length;
    return { sum, pendingSum, count: filtered.length, applied, pending: filtered.length - applied };
  }, [filtered]);

  const resetFilters = () => {
    setFilterEmployee("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterStatus("all");
  };

  const openAddModal = () => {
    setForm({
      employee_id: filterEmployee || employees[0]?._id || "",
      data: today,
      kwota: "",
      note: "",
      targetIdx: 4,
    });
    setShowAddModal(true);
  };

  const submitAdd = async (e) => {
    e?.preventDefault();
    if (!form.employee_id || !form.kwota || Number(form.kwota) <= 0) {
      alert("Wybierz pracownika i podaj kwotę (>0).");
      return;
    }
    const target = targetOptions[form.targetIdx];
    if (!target) {
      alert("Nieprawidłowy okres.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/employee-pozyczka`, {
        employee_id: form.employee_id,
        data: form.data,
        kwota: parseFloat(form.kwota),
        target_year: target.year,
        target_month: target.month,
        target_period: target.period,
        note: form.note || null,
      });
      setShowAddModal(false);
      await fetchDrafts();
    } catch (err) {
      alert("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (draft) => {
    if (!window.confirm(
      `Usunąć pożyczkę ${draft.employee_name} z dnia ${draft.data} (${fmtPLN(draft.kwota)} zł)?` +
      (draft.applied ? "\n\n⚠️ Ten wpis był już zastosowany do wypłaty — usunięcie nie cofnie odliczenia z istniejącej wypłaty!" : "")
    )) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/employee-pozyczka/${draft.id}`);
      await fetchDrafts();
    } catch (err) {
      alert("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`${API}/employee-pozyczka/${editing.id}`, {
        data: editing.data,
        kwota: parseFloat(editing.kwota) || 0,
        target_year: parseInt(editing.target_year, 10),
        target_month: parseInt(editing.target_month, 10),
        target_period: editing.target_period,
        note: editing.note || null,
      });
      setEditing(null);
      await fetchDrafts();
    } catch (err) {
      alert("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-pozyczki-pracownikow-tab">
      {/* Top action bar */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Wallet className="w-6 h-6 text-purple-600" weight="duotone" />
          Pożyczki pracowników
        </h2>
        <button
          onClick={openAddModal}
          className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold flex items-center gap-2 shadow-sm transition-colors"
          data-testid="admin-add-pozyczka-btn"
        >
          <Plus className="w-4 h-4" />
          Dodaj pożyczkę dla pracownika
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <FunnelSimple className="w-5 h-5 text-purple-600" weight="duotone" />
          <h3 className="font-semibold text-slate-800">Filtry</h3>
          <button
            onClick={resetFilters}
            className="ml-auto text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md flex items-center gap-1.5 text-slate-700"
            data-testid="reset-pozyczki-filters-btn"
          >
            <ArrowsCounterClockwise className="w-3.5 h-3.5" />
            Wyczyść filtry
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Pracownik
            </label>
            <select
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              className="input-field w-full"
              data-testid="pozyczki-filter-employee"
            >
              <option value="">Wszyscy pracownicy</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Data od
            </label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="input-field w-full"
              data-testid="pozyczki-filter-date-from"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Data do
            </label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="input-field w-full"
              data-testid="pozyczki-filter-date-to"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-field w-full"
              data-testid="pozyczki-filter-status"
            >
              <option value="all">Wszystkie</option>
              <option value="pending">Tylko oczekujące</option>
              <option value="applied">Tylko zastosowane</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Liczba wpisów
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1 tabular-nums" data-testid="pozyczki-total-count">
            {totals.count}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Oczekujące (zł)
          </div>
          <div className="text-2xl font-bold text-amber-700 mt-1 tabular-nums" data-testid="pozyczki-pending-sum">
            {fmtPLN(totals.pendingSum)}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Zastosowane
          </div>
          <div className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums" data-testid="pozyczki-total-applied">
            {totals.applied}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Suma łącznie
          </div>
          <div className="text-2xl font-bold text-purple-600 mt-1 tabular-nums" data-testid="pozyczki-total-sum">
            {fmtPLN(totals.sum)} zł
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-purple-600" weight="duotone" />
            <h3 className="font-semibold text-slate-800">
              Pożyczki pracowników
            </h3>
          </div>
          <span className="text-xs text-slate-500">
            Auto-odświeżanie co 30s
          </span>
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate-400">Ładowanie...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            Brak wpisów dla wybranych filtrów.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Pracownik</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Data</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Kwota</th>
                    <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Rozliczone</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Wypłata docelowa</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Notatka</th>
                    <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Dodano</th>
                    <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider w-28">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((d) => (
                    <tr
                      key={d.id}
                      className="border-t border-slate-100 hover:bg-slate-50"
                      data-testid={`admin-pozyczka-row-${d.id}`}
                    >
                      <td className="py-3 px-4 font-semibold text-slate-800">
                        {d.employee_name || "—"}
                      </td>
                      <td className="py-3 px-4 text-slate-700">{d.data}</td>
                      <td className="py-3 px-4 text-right tabular-nums font-bold text-slate-800">
                        {fmtPLN(d.kwota)} zł
                      </td>
                      <td className="py-3 px-4 text-center">
                        {d.applied ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600 inline-block" weight="fill" />
                        ) : (
                          <span className="text-slate-300 text-xl">○</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {MONTHS_PL[(d.target_month || 1) - 1]} {d.target_year} — {d.target_period}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500 max-w-xs truncate">
                        {d.note || "—"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {d.applied ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3.5 h-3.5" weight="fill" />
                            Zastosowano
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                            <Clock className="w-3.5 h-3.5" weight="fill" />
                            Oczekuje
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500">
                        {d.created_at
                          ? new Date(d.created_at).toLocaleString("pl-PL", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => setEditing({ ...d })}
                          disabled={saving}
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-700 mr-1"
                          title="Edytuj"
                          data-testid={`admin-pozyczka-edit-${d.id}`}
                        >
                          <PencilSimple className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDelete(d)}
                          disabled={saving}
                          className="p-1.5 hover:bg-rose-100 rounded text-rose-600"
                          title="Usuń"
                          data-testid={`admin-pozyczka-delete-${d.id}`}
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              testId="pozyczki-pracownikow-pagination"
            />
          </>
        )}
      </div>

      {/* Add Pożyczka Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          data-testid="admin-pozyczka-add-modal"
          onClick={() => !saving && setShowAddModal(false)}
        >
          <form
            onSubmit={submitAdd}
            className="bg-white rounded-xl border border-border max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="font-bold text-lg text-slate-800">
                Nowa pożyczka dla pracownika
              </h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="p-1.5 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
                  Pracownik *
                </label>
                <select
                  value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  className="input-field w-full"
                  required
                  data-testid="admin-pozyczka-employee-select"
                >
                  <option value="">— Wybierz pracownika —</option>
                  {employees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
                    Data *
                  </label>
                  <input
                    type="date"
                    value={form.data}
                    onChange={(e) => setForm({ ...form, data: e.target.value })}
                    className="input-field w-full"
                    required
                    data-testid="admin-pozyczka-date-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
                    Kwota (zł) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.kwota}
                    onChange={(e) => setForm({ ...form, kwota: e.target.value })}
                    className="input-field w-full"
                    placeholder="np. 500"
                    required
                    data-testid="admin-pozyczka-kwota-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
                  Notatka (opcjonalnie)
                </label>
                <input
                  type="text"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="input-field w-full"
                  placeholder="np. Zaliczka na remont"
                  data-testid="admin-pozyczka-note-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">
                  Wypłata docelowa *
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {targetOptions.map((opt, i) => (
                    <label
                      key={i}
                      className={`flex items-center gap-3 p-2.5 border rounded-lg cursor-pointer transition-colors ${
                        form.targetIdx === i
                          ? "border-purple-500 bg-purple-50"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`admin-pozyczka-target-${i}`}
                    >
                      <input
                        type="radio"
                        checked={form.targetIdx === i}
                        onChange={() => setForm({ ...form, targetIdx: i })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                disabled={saving}
                className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-semibold"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2"
                data-testid="admin-pozyczka-submit-btn"
              >
                {saving ? (
                  <>
                    <CircleNotch className="w-4 h-4 animate-spin" /> Zapisuję...
                  </>
                ) : (
                  "Zapisz pożyczkę"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-border max-w-md w-full">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Wallet className="w-5 h-5 text-purple-600" weight="duotone" />
                Edytuj pożyczkę
              </h3>
              <button
                onClick={() => setEditing(null)}
                className="p-1 hover:bg-slate-100 rounded"
                data-testid="admin-pozyczka-edit-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Pracownik</div>
                <div className="text-sm font-bold text-slate-800">{editing.employee_name || editing.employee_id}</div>
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Data pożyczki</label>
                <input
                  type="date"
                  value={editing.data || ""}
                  onChange={(e) => setEditing({ ...editing, data: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded"
                  data-testid="admin-pozyczka-edit-data"
                />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Kwota (zł)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editing.kwota || ""}
                  onChange={(e) => setEditing({ ...editing, kwota: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded font-mono"
                  data-testid="admin-pozyczka-edit-kwota"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Rok wypłaty</label>
                  <input
                    type="number"
                    value={editing.target_year || ""}
                    onChange={(e) => setEditing({ ...editing, target_year: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded"
                    data-testid="admin-pozyczka-edit-year"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Mies.</label>
                  <select
                    value={editing.target_month || 1}
                    onChange={(e) => setEditing({ ...editing, target_month: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded"
                    data-testid="admin-pozyczka-edit-month"
                  >
                    {MONTHS_PL.map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Okres</label>
                  <select
                    value={editing.target_period || "01-15"}
                    onChange={(e) => setEditing({ ...editing, target_period: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded"
                    data-testid="admin-pozyczka-edit-period"
                  >
                    <option value="01-15">01-15</option>
                    <option value="16-30">16-30</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Notatka</label>
                <input
                  type="text"
                  value={editing.note || ""}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded"
                  data-testid="admin-pozyczka-edit-note"
                />
              </div>
              {editing.applied && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠️ Ten wpis już został zastosowany do wypłaty. Edycja zmieni szczegóły wpisu, ale NIE zaktualizuje już obliczonej wypłaty.
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                <button
                  onClick={() => setEditing(null)}
                  disabled={saving}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded text-sm font-semibold"
                  data-testid="admin-pozyczka-edit-cancel"
                >
                  Anuluj
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-semibold disabled:opacity-50"
                  data-testid="admin-pozyczka-edit-save"
                >
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
